import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import CustomCampaignAudienceStep from './steps/CustomCampaignAudienceStep';
import CustomCampaignContentStep from './steps/CustomCampaignContentStep';
import CustomCampaignNameStep from './steps/CustomCampaignNameStep';
import CustomCampaignScheduleStep from './steps/CustomCampaignScheduleStep';
import CustomCampaignChooseNextStep from './steps/CustomCampaignChooseNextStep';
import CustomCampaignPublishStep from './steps/CustomCampaignPublishStep';
import CustomCampaignTimeDelayStep from './steps/CustomCampaignTimeDelayStep';
import CustomCampaignChooseSendTypeStep from './steps/CustomCampaignChooseSendTypeStep';
import CustomCampaignSplitCriteriaStep from './steps/CustomCampaignSplitCriteriaStep';
import CustomCampaignChooseSplitAction from './steps/CustomCampaignChooseSplitAction';
import CustomCampaignSetupABTestStep from './steps/CustomCampaignSetupABTestStep';

type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    contentPreviewId?: number;
    customContinueHandler?: (() => void) | null; // New: custom continue handler
    dispatchCampaignAction: React.Dispatch<any>;
    initialContentSubStep?: 'selecting' | 'scheduling'; // New: for controlling initial content sub-step
    navigateToABTestScheduling?: (emailIndex: number) => void; // New: for A/B test scheduling navigation
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
    setContentPreviewId: (id: number) => void;
    setCustomContinueHandler?: (handler: (() => void) | null) => void; // New: setter for custom continue handler
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    onContentUnavailableChange?: (isUnavailable: boolean, unavailableContentId?: number | null) => void;
};

const CustomCampaignForm = ({
    activeSendIndex,
    activeStep,
    campaignState,
    contentPreviewId,
    customContinueHandler,
    dispatchCampaignAction,
    initialContentSubStep,
    navigateToABTestScheduling,
    setActiveSendIndex,
    setActiveStep,
    setContentPreviewId,
    setCustomContinueHandler,
    setIsContinueEnabled,
    setNextStep,
    onContentUnavailableChange,
}: Props) => {
    switch (activeStep) {
        case CustomCampaignSteps.NAME_CAMPAIGN:
            return (
                <CustomCampaignNameStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SELECT_AUDIENCE:
            return (
                <CustomCampaignAudienceStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SELECT_CONTENT:
            return (
                <CustomCampaignContentStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    contentPreviewId={contentPreviewId}
                    dispatchCampaignAction={dispatchCampaignAction}
                    initialContentSubStep={initialContentSubStep}
                    setActiveSendIndex={setActiveSendIndex}
                    setContentPreviewId={setContentPreviewId}
                    setCustomContinueHandler={setCustomContinueHandler}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                    onContentUnavailableChange={onContentUnavailableChange}
                />
            );
        case CustomCampaignSteps.CHOOSE_NEXT_STEP:
            return (
                <CustomCampaignChooseNextStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.SET_TIME_DELAY:
            return (
                <CustomCampaignTimeDelayStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.CHOOSE_SEND_TYPE:
            return (
                <CustomCampaignChooseSendTypeStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.CONDITIONAL_SPLIT:
            return (
                <CustomCampaignSplitCriteriaStep
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SELECT_PATH_ACTION:
            return (
                <CustomCampaignChooseSplitAction
                    activeSendIndex={activeSendIndex}
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        case CustomCampaignSteps.SCHEDULE_CURATION:
            return (
                <CustomCampaignScheduleStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.SETUP_A_B_TEST:
            return (
                <CustomCampaignSetupABTestStep
                    campaignState={campaignState}
                    dispatchCampaignAction={dispatchCampaignAction}
                    setActiveStep={setActiveStep}
                    setIsContinueEnabled={setIsContinueEnabled}
                    setNextStep={setNextStep}
                />
            );
        case CustomCampaignSteps.PUBLISH_CAMPAIGN:
            return (
                <CustomCampaignPublishStep
                    campaignState={campaignState}
                    navigateToABTestScheduling={navigateToABTestScheduling}
                    setActiveSendIndex={setActiveSendIndex}
                    setActiveStep={setActiveStep}
                />
            );
        default:
            return <div>This step is invalid!</div>;
    }
};

export default CustomCampaignForm;
